'use client'

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { Shard } from './Shard'
import { getPseudoRandomShardList } from './Pixelator/shards'
import { cn } from '@/utilities/ui'
import { debounce } from 'lodash'
import { OverflowNegativeMargin } from './OverflowNegativeMargin'

const cellSize = 30
const randomUpdateInterval = 3000

interface ShardGridProps {
  className?: string
  minGapSize?: number
  enableRandomUpdates?: boolean
}

const negativeMargin = 20

export function ShardGrid({
  className,
  minGapSize = 50,
  enableRandomUpdates = true,
}: ShardGridProps) {
  const gridRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [gap, setGap] = useState(16) // Default gap in pixels
  const [dimensions, setDimensions] = useState({ cols: 6, rows: 2, count: 12 })
  const [isLayoutCalculated, setIsLayoutCalculated] = useState(false)
  const [shardList, setShardList] = useState(() => getPseudoRandomShardList(dimensions.count))
  const [animatingShardIndex, setAnimatingShardIndex] = useState<number | null>(null)
  const [isNewShard, setIsNewShard] = useState(false)

  // Keep shard list in sync with dimensions
  useEffect(() => {
    setShardList(getPseudoRandomShardList(dimensions.count))
  }, [dimensions.count])

  // Animate and update a specific shard by index
  const animateAndUpdateShard = useCallback((index: number) => {
    setShardList((currentList) => {
      const newList = [...currentList]
      const newShards = getPseudoRandomShardList(1)
      newList[index] = newShards[0]!
      setAnimatingShardIndex(index)
      setIsNewShard(false)
      setTimeout(() => {
        setIsNewShard(true)
        setTimeout(() => {
          setAnimatingShardIndex(null)
        }, 100)
      }, 100)
      return newList
    })
  }, [])

  // Update a random shard with a new style
  const updateRandomShard = useCallback(() => {
    setShardList((currentList) => {
      const newList = [...currentList]
      const randomIndex = Math.floor(Math.random() * currentList.length)
      const newShards = getPseudoRandomShardList(1)
      // We know getPseudoRandomShardList always returns a non-empty array
      newList[randomIndex] = newShards[0]!

      // Start animation
      setAnimatingShardIndex(randomIndex)
      setIsNewShard(false)

      // After 100ms, show the new shard
      setTimeout(() => {
        setIsNewShard(true)
        // After another 100ms, end the animation
        setTimeout(() => {
          setAnimatingShardIndex(null)
        }, 100)
      }, 100)

      return newList
    })
  }, [])

  // Effect for random shard updates
  useEffect(() => {
    if (!enableRandomUpdates) return

    const interval = setInterval(updateRandomShard, randomUpdateInterval)
    return () => clearInterval(interval)
  }, [enableRandomUpdates, updateRandomShard])

  // Calculate the optimal layout based on container dimensions
  const calculateLayout = useCallback(() => {
    if (!containerRef.current || !gridRef.current) return

    const container = containerRef.current
    const containerHeight = container.offsetHeight || 200 // Default height

    // Calculate how many columns will fit at 30px each with minimum gap
    const cellWidth = cellSize

    // Exclude padding from width calculation
    const style = window.getComputedStyle(container)
    const paddingLeft = parseFloat(style.paddingLeft)
    const paddingRight = parseFloat(style.paddingRight)
    const contentWidth = container.clientWidth - paddingLeft - paddingRight

    // First pass: calculate max columns that fit with minimum gap
    const maxCols = Math.floor((contentWidth + minGapSize) / (cellWidth + minGapSize))

    // Second pass: calculate optimal gap given the number of columns
    const cols = Math.max(1, maxCols)
    const totalCellWidth = cols * cellWidth
    const availableSpace = contentWidth - totalCellWidth

    // Calculate gap size (available space divided by number of gaps)
    const calculatedGap = cols > 1 ? availableSpace / (cols - 1) : minGapSize
    const finalGap = Math.max(minGapSize, calculatedGap)

    // Calculate how many rows will fit with the same gap
    const cellHeight = cellSize
    const rowHeight = cellHeight + finalGap
    const maxRows = Math.floor((containerHeight + finalGap) / rowHeight)
    const rows = Math.max(1, maxRows)

    // Update state
    setGap(finalGap)
    setDimensions({
      cols,
      rows,
      count: cols * rows,
    })
    setIsLayoutCalculated(true)
  }, [minGapSize])

  // Create debounced version of the layout calculation with lodash
  const debouncedCalculateLayout = useCallback(
    debounce(calculateLayout, 200), // Debounce to 200ms
    [calculateLayout],
  )

  useEffect(() => {
    // Initial calculation (not debounced)
    calculateLayout()

    // Add debounced resize listener
    window.addEventListener('resize', debouncedCalculateLayout)

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedCalculateLayout)
      // Cancel any pending debounced calls
      debouncedCalculateLayout.cancel?.()
    }
  }, [calculateLayout, debouncedCalculateLayout])

  return (
    <OverflowNegativeMargin margin={negativeMargin}>
      <div
        ref={containerRef}
        className={cn('w-full h-full flex items-center justify-center', className)}
        suppressHydrationWarning
      >
        <div
          ref={gridRef}
          className={cn('grid w-full', !isLayoutCalculated && 'invisible')}
          style={{
            gridTemplateColumns: `repeat(${dimensions.cols}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${dimensions.rows}, ${cellSize}px)`,
            gap: `${gap}px`,
            justifyContent: 'space-between', // Distribute columns evenly
          }}
        >
          {shardList.map((shard, index) => (
            <div
              key={index}
              className={cn(
                'transition-all duration-200 cursor-pointer',
                animatingShardIndex === index && !isNewShard && 'scale-0 opacity-0',
                animatingShardIndex === index && isNewShard && 'scale-150',
              )}
              onClick={() => {
                if (animatingShardIndex === index) return
                animateAndUpdateShard(index)
              }}
            >
              <Shard shard={shard} />
            </div>
          ))}
        </div>
      </div>
    </OverflowNegativeMargin>
  )
}
